import { formatDate } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ReporteService } from 'src/app/services/reportes/reporte.service';
import { ExcelService } from 'src/app/services/excel/excel.service';
import { PlanService } from 'src/app/services/planService/plan.service';
import Swal from 'sweetalert2';

@Component({
  selector: 'app-excess-report-page',
  templateUrl: './excess-report-page.component.html',
  styleUrls: ['./excess-report-page.component.scss']
})
export class ReportExcessPageComponent implements OnInit {
  public loader = true;
  public data_source: any;
  public planLists: any[] = [];
  public excedentsInfo: any = {};

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private reportService: ReporteService,
    private excelService: ExcelService,
    private planService: PlanService
  ) {}

  async ngOnInit() {
    this.planLists = await this.getPlans();
    this.excedentsInfo = await this.getExcedents();
    const id = this.route.snapshot.paramMap.get('id') ?? '';
    if (id) {
      await this.findReport(id);
    }
    this.loader = false;
  }

  private async getPlans() {
    try {
      const response = await this.planService.getPlans();
      return response.data || [];
    } catch (error) {
      console.error(error);
      Swal.fire({ icon: 'error', title: 'Error al cargar los planes' });
      return [];
    }
  }

  private async getExcedents() {
    try {
      const response = await this.planService.getExcendents();
      return response.data || [];
    } catch (error) {
      console.error(error);
      Swal.fire({ icon: 'error', title: 'Error al cargar los excedentes' });
      return [];
    }
  }

 async findReport(id: string) {
  try {
    const response = await this.reportService.findExcessReport(id);

    response.reportData.sort((a: any, b: any) => {
      const clientA = (a?.nombre_empresa || a?.cliente || '').toString();
      const clientB = (b?.nombre_empresa || b?.cliente || '').toString();
      return clientA.localeCompare(clientB);
    });

    const config = this.excedentsInfo[0];

    const excedentMinutesBlock = config.excedentMinutesBlock;
    const excedentHoursBlock = config.excedentHoursBlock;
    const excedentPrice = config.excedentPrice;

    // 🔹 Jerarquía de planes (por nivel)
    const PLAN_LEVELS = ["LIGHT", "PLATA", "ORO", "DIAMANTE"];

    const formattedData = response.reportData.map((item: any) => {
      const planActual = this.planLists.find(
        (p: any) => (p.title || p.name) === (item.plan || '').toString()
      );

      const planTitle = planActual?.title || planActual?.name || '';
      const planMinutes = Number(planActual?.minutes || 0);
      const planHours = Number(planActual?.time || planActual?.hours || 0);
      const planPrice = Number(planActual?.precio || planActual?.price || 0);

      const secondsLlamadas = Number(item.seconds_llamadas || 0);
      const secondsTareas = Number(item.seconds_tareas || 0);

      const minutesUsed = Math.round(secondsLlamadas / 60);
      const hoursUsed = Number((secondsTareas / 3600).toFixed(2));

      const excessMinutes = Math.max(0, minutesUsed - planMinutes);
      const excessHours = Math.max(0, hoursUsed - planHours);

      const blocksMin = excessMinutes > 0 ? Math.ceil(excessMinutes / excedentMinutesBlock) : 0;
      const blocksHr = excessHours > 0 ? Math.ceil(excessHours / excedentHoursBlock) : 0;

      const totalExcess = Math.max(blocksMin, blocksHr);
      const excessAmount = totalExcess * excedentPrice;
      const totalToPay = planPrice + excessAmount;

      // 🔹 Determinar nivel del plan actual
      const baseName = planTitle.replace(/\b20\d{2}\b/g, '').replace(/\s+/g, ' ').trim().toUpperCase();
      const currentLevelIndex = PLAN_LEVELS.findIndex(l => baseName.includes(l));

      let recommendedUpgrade = '';

      if (totalExcess > 0 && currentLevelIndex >= 0 && currentLevelIndex < PLAN_LEVELS.length - 1) {
        const nextLevel = PLAN_LEVELS[currentLevelIndex + 1];

        // Buscar planes del siguiente nivel (ej: ORO si está en PLATA)
        const nextLevelPlans = this.planLists
          .filter(p => (p.title || '').toUpperCase().includes(nextLevel))
          .map(p => ({
            name: p.title || p.name,
            price: Number(p.precio || p.price || 0),
            hours: Number(p.time || p.hours || 0),
            minutes: Number(p.minutes || 0),
            year: Number(((p.title || '').match(/\b(20\d{2})\b/) || [0, 0])[1]) || 0,
          }))
          .sort((a, b) => b.year - a.year || a.price - b.price);

        // Elegir el plan más reciente
        const nextPlan = nextLevelPlans[0];

        // Evaluar si conviene el cambio
        if (nextPlan && totalToPay > nextPlan.price) {
          const ahorro = (totalToPay - nextPlan.price).toLocaleString('es-MX', {
            style: 'currency',
            currency: 'MXN',
          });
          recommendedUpgrade = `Subir a "${nextPlan.name}" ahorro de ${ahorro}`;
        }
      }

      return {
        client: item.nombre_empresa || item.cliente || '',
        plan: item.plan || '',
        plan_minutes: planMinutes,
        plan_hours: planHours,
        plan_price: planPrice,
        minutesUsed,
        hoursUsed,
        excessMinutes,
        excessHours: excessHours.toFixed(2),
        totalExcess,
        excessAmount,
        totalToPay,
        recommendedUpgrade,
      };
    });

    this.data_source = {
      ...response,
      reportData: formattedData,
    };
  } catch (error) {
    console.error(error);
    Swal.fire({
      icon: 'error',
      title: 'Error al cargar el reporte',
      text: 'No fue posible procesar los datos del reporte de excedentes.',
    });
  }
}

  back() {
    this.router.navigate(['/excess-report']);
  }

  async excel() {
  try {
    const headers = [
      "Cliente",                   
      "Plan",                      
      "Minutos Contratados",       
      "Horas del Contratados",     
      "Precio Plan",               
      "Minutos Llamadas",          
      "Horas Trabajadas",          
      "Excedente Llamadas",        
      "Excedente Horas Trabajadas",
      "Bloque Excedentes",         
      "Monto Excedente",           
      "Total a Pagar",             
      "Recomendación"              
    ];

    const dataToPrint = this.data_source.reportData.map((r: any) => {
      const formatCurrency = (value: number) =>
        `$${value.toLocaleString('es-MX', { minimumFractionDigits: 2 })}`;

      return {
        "0": r.client,
        "1": r.plan,
        "2": r.plan_minutes,
        "3": r.plan_hours,
        "4": formatCurrency(r.plan_price),
        "5": r.minutesUsed,
        "6": r.hoursUsed,
        "7": r.excessMinutes,
        "8": r.excessHours,
        "9": Math.floor(r.totalExcess), 
        "10": formatCurrency(r.excessAmount),
        "11": formatCurrency(r.totalToPay),
        "12": r.recommendedUpgrade || ""
      };
    }) as any;

    // Calcular totales
    const totalBloqueExcedentes = this.data_source.reportData.reduce((sum: number, r: any) => 
      sum + Math.floor(r.totalExcess), 0);
    
    const totalMontoExcedente = this.data_source.reportData.reduce((sum: number, r: any) => 
      sum + r.excessAmount, 0);

    const totalTotalToPay = this.data_source.reportData.reduce((sum: number, r: any) => 
      sum + r.totalToPay, 0);

    const formatCurrency = (value: number) =>
      `$${value.toLocaleString('es-MX', { minimumFractionDigits: 2 })}`;

    dataToPrint.__excedentInfo = {
      price: this.excedentsInfo[0]?.excedentPrice || 0,
      minutes: this.excedentsInfo[0]?.excedentMinutesBlock || 0,
      hours: this.excedentsInfo[0]?.excedentHoursBlock || 0
    };

    const fileName =
      "Reporte Excedentes del " +
      formatDate(this.data_source.startDate, 'dd MMMM yyyy', 'es-MX') +
      ' al ' +
      formatDate(this.data_source.endDate, 'dd MMMM yyyy', 'es-MX');

    const totalesArray = new Array(headers.length).fill("-");
    totalesArray[8] = totalBloqueExcedentes;
    totalesArray[9] = formatCurrency(totalMontoExcedente);
    totalesArray[10] = formatCurrency(totalTotalToPay);

    await this.excelService._createReport(
      dataToPrint,
      headers,
      fileName,
      "REPORTE DE EXCEDENTES",
      totalesArray,
      [7, 8],
      []
    );
    
  } catch (error) {
    console.error(error);
    Swal.fire({
      icon: 'error',
      title: 'Ocurrió un error al exportar el Excel'
    });
  }
}
}

